import Alert from '@vromo/ui/components/Alert';
import Button from '@vromo/ui/components/Button';
import Modal, { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import { endOfDay, startOfDay, differenceInCalendarDays } from 'date-fns';
import { LocalStorage } from '@vromo/models';
import sortBy from 'lodash/sortBy';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDefaultFilters } from '../../ReportRoot.lazy';
import { FilterObject, FilterOptions, Filters } from '../../utils/reportTypes';
import FilterDateRange from './components/FilterDateRange';
import FilterDropdown from './components/FilterDropdown';
import FilterInput from './components/FilterInput/FilterInput';
import { qaLabelReports } from '../../../../../../common-qa-labels/reports';

interface Props {
    isOpen: boolean;
    filters: Filters;
    filterOptions: FilterOptions;
    applyFilters: (filters: Filters) => void;
    onClose: () => void;
}

const ReportFilterModal = observer<Props>(
    ({ isOpen, applyFilters, filterOptions, filters, onClose }) => {
        const { t } = useTranslation();
        const translate = (key: string, params?: any) => t(`reportsFilterModal.${key}`, params);
        const [showAlert, setShowAlert] = useState(false);
        const [currentFilters, setCurrentFilters] = useState<Filters>(filters);

        useEffect(() => {
            if (isOpen) setCurrentFilters(filters);
        }, [isOpen]);

        const setFilter = useCallback((filterVal: any) => {
            setCurrentFilters((prevFilters) => ({
                ...prevFilters,
                ...filterVal
            }));
        }, []);

        const applyFilter = () => {
            const { startDate, endDate } = currentFilters;

            const withinReportDateRange =
                differenceInCalendarDays(endDate.valueOf(), startDate.valueOf()) <= 62;

            if (!withinReportDateRange) return setShowAlert(true);

            if (!!startDate !== !!endDate) return setShowAlert(true);

            setShowAlert(false);
            applyFilters(currentFilters);
            onClose();
        };

        const handleOnClose = () => {
            setShowAlert(false);
            onClose();
        };

        const resetFilters = useCallback(() => {
            setShowAlert(false);
            setCurrentFilters(getDefaultFilters());
        }, []);

        const sortOptions = useCallback(
            (options: FilterObject[]) => sortBy(options, ['label']),
            []
        );

        const alertMessage = translate('dateRangeError');

        const jobStatusOptions = useMemo(
            () =>
                sortOptions([
                    { label: 'Completed', value: 'completed' },
                    { label: 'Cancelled', value: 'cancelled' }
                ]),
            [sortOptions]
        );

        const { groups, drivers, sites, policies, campaigns } = filterOptions;

        const teamsOptions = useMemo(() => sortOptions(groups), [sortOptions, groups]);
        const driverOptions = useMemo(() => sortOptions(drivers), [sortOptions, drivers]);
        const sitesOptions = useMemo(() => sortOptions(sites), [sortOptions, sites]);
        const autoDispatcherOptions = useMemo(() => sortOptions(policies), [sortOptions, policies]);
        const campaignsOptions = useMemo(() => sortOptions(campaigns), [sortOptions, campaigns]);

        const onChangeJobName = useCallback(
            ({ target: { value } }) => setFilter({ jobName: value }),
            [setFilter]
        );
        const onChangeJobStatus = useCallback((e) => setFilter({ jobStatus: e }), [setFilter]);

        const setFilters = useCallback(
            (selected: ChangeEvent<HTMLSelectElement>, paramName: string) =>
                setFilter({ [paramName]: selected !== null ? selected : [] }),
            [setFilter]
        );

        const onChangeTeams = useCallback((s) => setFilters(s, 'teams'), [setFilters]);
        const onChangeDrivers = useCallback((s) => setFilters(s, 'vehicles'), [setFilters]);
        const onChangeSites = useCallback((s) => setFilters(s, 'sites'), [setFilters]);
        const onChangeAutoDispatcher = useCallback((s) => setFilters(s, 'policies'), [setFilters]);
        const onChangeCampaigns = useCallback((s) => setFilters(s, 'campaigns'), [setFilters]);

        const onDatesChange = useCallback(
            ({ startDate, endDate }) => {
                setShowAlert(false);

                setFilter({
                    startDate: startDate && startOfDay(startDate.valueOf()),
                    endDate: endDate && endOfDay(endDate.valueOf())
                });
            },
            [setFilter]
        );

        return (
            <Modal isOpen={isOpen} onRequestClose={handleOnClose}>
                <ModalHeader>
                    <Title>{translate('title')}</Title>
                </ModalHeader>
                <ModalContent style={{ overflowX: 'visible', maxWidth: 700 }}>
                    {showAlert ? <Alert danger>{alertMessage}</Alert> : null}
                    <FilterDateRange
                        startDate={currentFilters.startDate}
                        endDate={currentFilters.endDate}
                        onDatesChange={onDatesChange}
                    />
                    <FilterInput
                        label={t('job.jobName')}
                        placeholder={translate('jobNamePlaceholder')}
                        value={currentFilters.jobName}
                        onChange={onChangeJobName}
                    />
                    <FilterDropdown
                        label={translate('jobStatus')}
                        placeholder={translate('jobStatusPlaceholder')}
                        value={currentFilters.jobStatus}
                        options={jobStatusOptions}
                        onChange={onChangeJobStatus}
                        canSelectMultiple={false}
                    />
                    <FilterDropdown
                        label={translate('teams')}
                        placeholder={translate('teamsPlaceholder')}
                        value={currentFilters.teams}
                        options={teamsOptions}
                        onChange={onChangeTeams}
                    />
                    <FilterDropdown
                        label={translate('drivers')}
                        placeholder={translate('driversPlaceholder')}
                        value={currentFilters.vehicles}
                        options={driverOptions}
                        onChange={onChangeDrivers}
                    />
                    <FilterDropdown
                        label={translate('sites')}
                        placeholder={translate('sitesPlaceholder')}
                        value={currentFilters.sites}
                        options={sitesOptions}
                        onChange={onChangeSites}
                    />
                    <FilterDropdown
                        label={translate('autoDispatchers')}
                        placeholder={translate('autoDispatchersPlaceholder')}
                        value={currentFilters.policies}
                        options={autoDispatcherOptions}
                        onChange={onChangeAutoDispatcher}
                    />
                    <FilterDropdown
                        label={translate('campaigns')}
                        placeholder={translate('campaignsPlaceholder')}
                        value={currentFilters.campaigns}
                        options={campaignsOptions}
                        onChange={onChangeCampaigns}
                    />
                </ModalContent>
                <ModalActions>
                    <Button style={{ marginRight: 'auto' }} onClick={resetFilters}>
                        {translate('resetFilters')}
                    </Button>
                    <Button onClick={handleOnClose}>{translate('cancel')}</Button>
                    <Button
                        data-testid={qaLabelReports.applyFiltersButton}
                        primary
                        onClick={applyFilter}>
                        {translate('applyFilters')}
                    </Button>
                </ModalActions>
            </Modal>
        );
    }
);

export default memo(ReportFilterModal);
